/**
 * Staff detail: current tags, assignments history, 30-day chart, totals.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";

import api, { extractErr } from "../api";
import { BackLink, Card, PageHeader, StatCard, formatDate, Badge } from "../ui";

export default function StaffDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/stats/staff/${id}`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(extractErr(e)));
    }, [id]);

    if (err) return <div className="text-red-600 text-sm">{err}</div>;
    if (!data) return <div className="text-slate-500 text-sm">Caricamento…</div>;

    const chart = data.daily_30d.map((d) => ({ day: d.date.slice(5), Accessi: d.count }));

    return (
        <div data-testid="staff-detail-page">
            <BackLink to="/admin/collaboratori" label="Collaboratori" />
            <PageHeader
                title={`${data.staff.first_name} ${data.staff.last_name}`}
                description={data.staff.role || "Collaboratore"}
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard label="Totale accessi" value={data.total} testId="staff-total" />
                <StatCard
                    label="Tag attivi"
                    value={data.current_tags.length}
                    testId="staff-tags-count"
                />
                <StatCard
                    label="Ultimi 30 gg"
                    value={data.daily_30d.reduce((a, b) => a + b.count, 0)}
                    testId="staff-30d"
                />
                <StatCard
                    label="Stato"
                    value={data.staff.active ? "Attivo" : "Disattivato"}
                    testId="staff-status"
                />
            </div>

            <Card className="p-5 mb-6">
                <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-1">
                    Andamento
                </div>
                <div className="text-lg font-semibold mb-4">Accessi giornalieri · ultimi 30 gg</div>
                <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                        <BarChart data={chart} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                            <Bar dataKey="Accessi" fill="#0f172a" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Card className="overflow-hidden mb-6">
                <div className="px-5 py-4 border-b border-slate-200">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500">Tag correntemente assegnati</div>
                </div>
                {data.current_tags.length === 0 ? (
                    <div className="px-5 py-6 text-slate-500 text-sm">
                        Nessun tag NFC attualmente assegnato a questo collaboratore.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-5 py-3">Codice tag</th>
                                <th className="text-left px-5 py-3">Assegnato dal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.current_tags.map((t) => (
                                <tr key={t.tag_code} className="border-t border-slate-100">
                                    <td className="px-5 py-3 font-mono">{t.tag_code}</td>
                                    <td className="px-5 py-3">{formatDate(t.start_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <Card className="overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500">
                        Storico assegnazioni
                    </div>
                </div>
                {data.history.length === 0 ? (
                    <div className="px-5 py-6 text-slate-500 text-sm">Nessuno storico.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-5 py-3">Tag</th>
                                <th className="text-left px-5 py-3">Dal</th>
                                <th className="text-left px-5 py-3">Al</th>
                                <th className="text-left px-5 py-3">Stato</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.history.map((h) => (
                                <tr key={h.id} className="border-t border-slate-100">
                                    <td className="px-5 py-3 font-mono">{h.tag_code}</td>
                                    <td className="px-5 py-3">{formatDate(h.start_at)}</td>
                                    <td className="px-5 py-3">{h.end_at ? formatDate(h.end_at) : "—"}</td>
                                    <td className="px-5 py-3">
                                        {h.end_at ? (
                                            <Badge tone="neutral">chiuso</Badge>
                                        ) : (
                                            <Badge tone="success">in corso</Badge>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>
        </div>
    );
}
